import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {__} from "services/i18n";
import Title from "features/components/title";
import {func, string, array, oneOfType, object} from "prop-types";
import {TextField} from "features/components/text-field";
import {COMPANY_ROLES, EMPLOYEES, REFERENCE_SECTORS, ADMIN_ROLE_KEY} from "constants.js";
import DatePickerComponent from "features/components/date-picker";
import Select from "features/components/select";
import {
    CompanyValidationErrors,
    CompanyValidator,
    EmailTextFieldValidator,
    PhoneTextFieldValidator
} from "services/utils/validations";
import CompanyAddressForm, {CompanyAddressFormBaseState} from "features/forms/company-upsert/company-address";
import Button from "features/components/button";
import {isEqual} from "lodash";
import TextArea from "features/components/text-area";
import DocumentLoader from "features/components/document-loader";
import {Icon} from "features/components/icon";
import Text from "features/components/text";
import ChipSelector from "features/components/chip-selector";
import CompanyProductForm, {CompanyProductFormBaseState} from "features/forms/company-upsert/company-product";
import {connect} from "react-redux";
import {downloadStaticData} from "store/reducer/static/reducer";
import {staticDataOsaOffices, staticDataSkills} from "store/reducer/static/selectors";
import Errors from "features/components/errors";

/* istanbul ignore next */
const mapStateToProps = state => ({
    skillsData: staticDataSkills(state),
    osaOffices: staticDataOsaOffices(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    downloadStaticData
};

export const CompanyUpsertForm = (props) => {
    const {id, name, onChange, downloadStaticData, skillsData, osaOffices} = props;
    const isEdit = !!id;

    const [nameVal, setName] = useState(props.name);
    const [shortNameVal, setShortName] = useState(props.shortName);
    const [vatVal, setVat] = useState(props.vat);
    const [adeCodeVal, setAdeCode] = useState(props.adeCode);
    const [creationYearVal, setCreationYear] = useState(props.creationYear);
    const [employeesVal, setEmployees] = useState(props.employees);
    const [osaCenterVal, setOsaCenter] = useState(props.osaCenter);
    const [referenceSectorVal, setReferenceSector] = useState(props.referenceSector);
    const [addressVal, setAddress] = useState([...props.address]);
    const [phoneVal, setPhone] = useState(props.phone);
    const [emailVal, setEmail] = useState(props.email);
    const [linkedinVal, setLinkedin] = useState(props.linkedin);
    const [twitterVal, setTwitter] = useState(props.twitter);
    const [websiteVal, setWebsite] = useState(props.website);
    const [descriptionVal, setDescription] = useState(props.description);
    const [logoVal, setLogo] = useState(props.logo);
    const [documentVal, setDocument] = useState(props.document);
    const [videoVal, setVideo] = useState(props.video);
    const [skillsVal, setSkills] = useState(props.skills);
    const [productsVal, setProducts] = useState([...props.products]);

    const [formErrors, setFormErrors] = useState([]);

    React.useEffect(() => {
        downloadStaticData()
    }, [downloadStaticData]);

    React.useEffect(() => {
        const company = {
            id: id,
            name: nameVal,
            shortName: shortNameVal,
            vat: vatVal,
            adeCode: adeCodeVal,
            creationYear: creationYearVal,
            employees: employeesVal,
            osaCenter: osaCenterVal,
            referenceSector: referenceSectorVal,
            address: addressVal,
            phone: phoneVal,
            email: emailVal,
            linkedin: linkedinVal,
            website: websiteVal,
            twitter: twitterVal,
            logo: logoVal,
            document: documentVal,
            video: videoVal,
            skills: skillsVal,
            products: productsVal,
            description: descriptionVal
        }
        onChange({
            ...company,
            valid: CompanyValidator(company)
        });
        setFormErrors(CompanyValidationErrors(company));
    }, [onChange, id, nameVal, shortNameVal, vatVal, adeCodeVal, creationYearVal, employeesVal, osaCenterVal, referenceSectorVal, addressVal,
            phoneVal, emailVal, linkedinVal, websiteVal, twitterVal, logoVal, documentVal, videoVal, skillsVal, productsVal, descriptionVal, setFormErrors]);

    const addCompanyAddress = useCallback(() => {
        const copy = [...addressVal];
        copy.push({...CompanyAddressFormBaseState});
        setAddress(copy);
    }, [addressVal, setAddress]);
    const setCompanyAddress = useCallback((index, value) => {
        const copy = [...addressVal];
        if(!isEqual(copy[index], value)) {
            copy[index] = value
            setAddress(copy);
        }
    }, [addressVal, setAddress]);
    const deleteCompanyAddress = useCallback((index) => {
        const copy = [...addressVal];
        delete copy[index];
        /*istanbul ignore next*/
        setAddress(copy.filter(i => !!i));
    }, [addressVal, setAddress]);

    const addCompanyProduct = useCallback(() => {
        const copy = [...productsVal];
        copy.push({...CompanyProductFormBaseState});
        setProducts(copy);
    }, [productsVal, setProducts]);
    const setCompanyProduct = useCallback((index, value) => {
        const copy = [...productsVal];
        if(!isEqual(copy[index], value)) {
            copy[index] = value
            setProducts(copy);
        }
    }, [productsVal, setProducts]);
    const deleteCompanyProduct = useCallback((index) => {
        const copy = [...productsVal];
        delete copy[index];
        /*istanbul ignore next*/
        setProducts(copy.filter(i => !!i));
    }, [productsVal, setProducts]);


    const title = useMemo(() => {
        if (isEdit) return __("forms.company-upsert.title-edit", [{key: "[companyName]", value: name}]);
        return __("forms.company-upsert.title-new")
    }, [isEdit, name]);

    return <div className="company-upsert-form">
        <Title size={24}>{title}</Title>
        <div>
            <Title size={18}>{__("forms.company-upsert.general-data")}</Title>
            <div className="form-field single">
                <TextField
                    type={"text"}
                    value={nameVal}
                    onChange={setName}
                    placeholder={__("text-fields.company-name.placeholder")}
                    disabled={isEdit}
                />
            </div>
            <div className="form-field">
                <TextField
                    type={"text"}
                    value={shortNameVal}
                    onChange={setShortName}
                    placeholder={__("text-fields.company-name-short.placeholder")}
                />
                <TextField
                    type={"text"}
                    value={COMPANY_ROLES.find(i => i.key === ADMIN_ROLE_KEY).value}
                    onChange={() => {}}
                    placeholder={__("text-fields.role.placeholder")}
                    disabled={true}
                />
                <TextField
                    type={"text"}
                    value={vatVal}
                    onChange={setVat}
                    placeholder={__("text-fields.vat.placeholder")}
                    disabled={isEdit}
                />
                <TextField
                    type={"text"}
                    value={adeCodeVal}
                    onChange={setAdeCode}
                    placeholder={__("text-fields.ade-code.placeholder")}
                />
            </div>
            <div className="form-field triple">
                <DatePickerComponent
                    type={"text"}
                    yearPicker={true}
                    value={creationYearVal}
                    onChange={setCreationYear}
                    dateFormat={"YYYY"}
                    placeholder={__("text-fields.creation-year.placeholder")}
                />
                <Select value={employeesVal}
                        values={EMPLOYEES}
                        onChange={setEmployees}
                        placeholder={__("text-fields.employees.placeholder")}
                />
                <Select value={osaCenterVal}
                        values={osaOffices}
                        onChange={setOsaCenter}
                        placeholder={__("text-fields.osa-center.placeholder")}
                />
            </div>
            <div className="form-field">
                <Select value={referenceSectorVal}
                        values={REFERENCE_SECTORS}
                        onChange={setReferenceSector}
                        placeholder={__("text-fields.reference-sector.placeholder")}
                />
            </div>
        </div>
        <div>
            <Title size={18}>{__("forms.company-upsert.company-address")}</Title>
            {addressVal.map((address, index) =>
                <CompanyAddressForm
                    showDeleteButton={index !== 0}
                    onDelete={() => deleteCompanyAddress(index)}
                    onChange={(value) => setCompanyAddress(index, value)}
                    key={index}
                    {...address}
                />
            )}
            <Button title={__("buttons.add-company-address")}
                    onClick={addCompanyAddress}
                    type={"outlined"}
            />
        </div>
        <div>
            <Title size={18}>{__("forms.company-upsert.contacts")}</Title>
            <div className="form-field">
                <TextField
                    type={"text"}
                    value={phoneVal}
                    onChange={setPhone}
                    placeholder={__("text-fields.company-phone.placeholder")}
                    validationFunc={PhoneTextFieldValidator}
                />
                <TextField
                    type={"text"}
                    value={emailVal}
                    onChange={setEmail}
                    placeholder={__("text-fields.company-email.placeholder")}
                    validationFunc={EmailTextFieldValidator}
                />
            </div>
            <div className="form-field single">
                <TextField
                    type={"text"}
                    value={linkedinVal}
                    onChange={setLinkedin}
                    icon={"linkedin"}
                    placeholder={__("text-fields.company-linkedin.placeholder")}
                />
            </div>
            <div className="form-field">
                <TextField
                    type={"text"}
                    value={twitterVal}
                    onChange={setTwitter}
                    icon={"twitter"}
                    placeholder={__("text-fields.company-twitter.placeholder")}
                />
                <TextField
                    type={"text"}
                    value={websiteVal}
                    onChange={setWebsite}
                    icon={"web"}
                    placeholder={__("text-fields.company-website.placeholder")}
                />
            </div>
        </div>
        <div>
            <Title size={18}>{__("forms.company-upsert.description")}</Title>
            <div className="form-field single">
                <TextArea value={descriptionVal}
                          onChange={setDescription}
                          placeholder={__("text-fields.company-description.placeholder")}
                />
            </div>
            <div className="form-field">
                <DocumentLoader
                    buttonTitle={__("buttons.upload-image")}
                    text={__("forms.company-upsert.upload-logo")}
                    accept={["image/*"]}
                    onChange={setLogo}
                    value={logoVal}
                />
                <DocumentLoader
                    buttonTitle={__("buttons.choose-doc")}
                    text={__("forms.company-upsert.upload-doc")}
                    accept={["application/pdf"]}
                    onChange={setDocument}
                    value={documentVal}
                />
            </div>
            <div className="form-field single">
                <TextField
                    type={"text"}
                    value={videoVal}
                    onChange={setVideo}
                    icon={"comment-video"}
                    placeholder={__("text-fields.company-video.placeholder")}
                />
            </div>
            <div className="row skill-title">
                <Icon>web</Icon>
                <Text size={16}>{__("forms.company-upsert.skills.skill.text")}</Text>
            </div>
            <ChipSelector
                placeholder={__("forms.company-upsert.skills.skill.placeholder")}
                data={skillsData}
                enableAddSearched={true}
                value={skillsVal}
                onChange={setSkills}
                maxItems={10}
            />
        </div>
        <div>
            <Title size={18}>{__("forms.company-upsert.products")}</Title>
            {productsVal.map((product, index) =>
                <CompanyProductForm
                    deleteButtonEnabled={index !== 0}
                    onDelete={() => deleteCompanyProduct(index)}
                    onChange={(value) => setCompanyProduct(index, value)}
                    key={index}
                    {...product}
                />
            )}
            <Button title={__("buttons.company-add-product")}
                    onClick={addCompanyProduct}
                    type={"outlined"}
            />
        </div>
        <Errors errors={formErrors} />
    </div>
};

CompanyUpsertForm.propTypes = {
    onChange: func.isRequired,
    name: string,
    shortName: string,
    vat: string,
    adeCode: string,
    creationYear: string,
    employees: string,
    osaCenter: string,
    referenceSector: string,
    address: array,
    phone: string,
    email: string,
    twitter: string,
    linkedin: string,
    website: string,
    description: string,
    logo: oneOfType([string, object]),
    document: string,
    video: string,
    skills: array,
    products: array
}

export const CompanyUpsertFormBaseState = {
    name: "",
    shortName: "",
    vat: "",
    adeCode: "",
    creationYear: "",
    employees: "",
    osaCenter: "",
    referenceSector: "",
    address: [{...CompanyAddressFormBaseState}],
    phone: "",
    email: "",
    twitter: "",
    linkedin: "",
    website: "",
    description: "",
    logo: null,
    document: null,
    video: "",
    skills: [],
    products: [{...CompanyProductFormBaseState}]
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyUpsertForm);